/**
 * @fileoverview Org portal: who a program's instructors contact.
 *
 * One box, two lists, each with its own Add button underneath:
 *
 * - **School contact**: the venue's people (a front office, a music lead),
 *   typed in, each a name, an email and a phone, any of which may be blank.
 * - **{Org} contact**: the organization's own people, picked from its admins.
 *   Picking someone shows that admin's name and login email to the program's
 *   instructors; that is the choice being made here.
 *
 * Saved together through the program's all-optional `PUT`, which also keeps
 * the old single contact fields in step for clients that still read them.
 * Instructors see both lists on their program page and hear, in their
 * Updates, that the contacts changed.
 */

import { useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Card, Icon, SectionHeader } from "@/components/studio/primitives";

/** Mirrors the backend's MAX_PROGRAM_CONTACTS, per list. */
export const MAX_CONTACTS = 10;

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3.5 py-[9px] font-sans text-[14px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

const EMPTY_SCHOOL = { name: "", email: "", phone: "" };

function adminName(a) {
  return [a.first_name, a.last_name].filter(Boolean).join(" ") || a.email;
}

function seedSchool(program) {
  const rows = (program.school_contacts || []).map((c) => ({
    name: c.name || "",
    email: c.email || "",
    phone: c.phone || "",
  }));
  return rows.length ? rows : [{ ...EMPTY_SCHOOL }];
}

function seedOrg(program) {
  const ids = program.org_contact_ids || [];
  return ids.length ? [...ids] : [""];
}

function RemoveButton({ label, onPress, testId }) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={label}
      data-testid={testId}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--text-tertiary)] hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)]"
    >
      <Icon name="x" size={16} />
    </button>
  );
}

/**
 * @param {{ program: object, orgName?: string, admins: Array, onSaved?: Function }} props
 */
export default function ProgramContactsCard({ program, orgName, admins, onSaved }) {
  const [school, setSchool] = useState(() => seedSchool(program));
  const [picked, setPicked] = useState(() => seedOrg(program));
  const [saving, setSaving] = useState(false);
  const org = orgName || "Organization";

  const setSchoolField = (index, key) => (e) =>
    setSchool((rows) => rows.map((row, i) => (i === index ? { ...row, [key]: e.target.value } : row)));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await orgApi.put(`/org/programs/${program.program_id}`, {
        school_contacts: school
          .map((c) => ({ name: c.name.trim(), email: c.email.trim(), phone: c.phone.trim() }))
          .filter((c) => c.name || c.email || c.phone),
        org_contact_ids: picked.filter(Boolean),
      });
      toast.success("Contacts saved");
      onSaved?.(res.data?.program);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save the contacts"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="mb-6" data-testid="program-contacts">
      <SectionHeader>Contacts</SectionHeader>
      <Card>
        <p className="mb-4 font-sans text-[13px] text-[var(--text-tertiary)]">
          Who instructors contact about this program. Shown on their program page, never on the public listing.
        </p>
        <div className="grid items-start gap-6 lg:grid-cols-2">
          {/* ---- School contact ------------------------------------------ */}
          <div className="min-w-0" data-testid="school-contacts">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-secondary)]">School contact</div>
            <div className="flex flex-col gap-3">
              {school.map((row, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-[12px] border border-[var(--studio-divider)] p-3"
                  data-testid={`school-contact-${i}`}
                >
                  <div className="grid min-w-0 flex-1 gap-2">
                    <input value={row.name} onChange={setSchoolField(i, "name")} placeholder="Name and role (e.g. Dana Reed, front office)" aria-label="School contact name" className={inputClass} data-testid={`school-contact-name-${i}`} />
                    <div className="grid gap-2 sm:grid-cols-2">
                      <input value={row.email} onChange={setSchoolField(i, "email")} type="email" placeholder="Email" aria-label="School contact email" className={inputClass} data-testid={`school-contact-email-${i}`} />
                      <input value={row.phone} onChange={setSchoolField(i, "phone")} placeholder="Phone" aria-label="School contact phone" className={inputClass} data-testid={`school-contact-phone-${i}`} />
                    </div>
                  </div>
                  <RemoveButton
                    label="Remove this school contact"
                    testId={`school-contact-remove-${i}`}
                    onPress={() => setSchool((rows) => (rows.length > 1 ? rows.filter((_, j) => j !== i) : [{ ...EMPTY_SCHOOL }]))}
                  />
                </div>
              ))}
            </div>
            {school.length < MAX_CONTACTS && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon="plus"
                className="mt-3"
                onPress={() => setSchool((rows) => [...rows, { ...EMPTY_SCHOOL }])}
                data-testid="school-contact-add"
              >
                Add
              </Button>
            )}
          </div>

          {/* ---- The organization's own people ----------------------------- */}
          <div className="min-w-0" data-testid="org-contacts">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-secondary)]">{org} contact</div>
            <div className="flex flex-col gap-3">
              {picked.map((adminId, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`org-contact-${i}`}>
                  <select
                    value={adminId}
                    onChange={(e) => setPicked((ids) => ids.map((id, j) => (j === i ? e.target.value : id)))}
                    aria-label={`${org} contact`}
                    className={`${inputClass} appearance-none`}
                    data-testid={`org-contact-select-${i}`}
                  >
                    <option value="">Choose someone from your team</option>
                    {admins.map((a) => (
                      <option
                        key={a.admin_id}
                        value={a.admin_id}
                        // One person once: picked in another row, not offered again.
                        disabled={a.admin_id !== adminId && picked.includes(a.admin_id)}
                      >
                        {adminName(a)}{a.email && adminName(a) !== a.email ? ` · ${a.email}` : ""}
                      </option>
                    ))}
                  </select>
                  <RemoveButton
                    label={`Remove this ${org} contact`}
                    testId={`org-contact-remove-${i}`}
                    onPress={() => setPicked((ids) => (ids.length > 1 ? ids.filter((_, j) => j !== i) : [""]))}
                  />
                </div>
              ))}
            </div>
            {picked.length < Math.min(MAX_CONTACTS, admins.length) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon="plus"
                className="mt-3"
                onPress={() => setPicked((ids) => [...ids, ""])}
                data-testid="org-contact-add"
              >
                Add
              </Button>
            )}
            <p className="mt-2 font-sans text-[12px] text-[var(--text-tertiary)]">
              Instructors see the name and email of each person you choose.
            </p>
          </div>
        </div>
        <div className="mt-5">
          <Button type="submit" disabled={saving} data-testid="program-contacts-save">
            {saving ? "Saving…" : "Save contacts"}
          </Button>
        </div>
      </Card>
    </form>
  );
}
